import {
  CHANNEL_LOGOS,
  getChannelFromScreenId,
  ImageHelper,
  type ITextComponentWithAssetsModel,
  useConfigurationSelector,
} from "@nf/common";
import { useTranslation } from "next-i18next";

import AppImage from "components/AppImage";
import Button from "components/Button";
import LinkWithLocale from "components/LinkWithLocale";

import ChevronRightIcon from "resources/icons/chevron-right.svg";

import styles from "./LandingPageChannels.module.scss";

interface LandingPageChannelsProps {
  component: ITextComponentWithAssetsModel;
}

const LandingPageChannels = ({ component }: LandingPageChannelsProps) => {
  const configuration = useConfigurationSelector();
  const { t } = useTranslation("translations");

  if (component.MediaList?.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.header}>{t("web_landing_page_channel_header")}</h3>

      <div className={styles.cards}>
        {component.MediaList?.map((media) => {
          const screenId = media.Action?.ScreenId;

          if (!screenId) {
            return null;
          }

          const imageUrl = ImageHelper.getHighlightsImageUrl(media?.Images);
          const icon = CHANNEL_LOGOS?.[screenId]?.compact;
          const channel = getChannelFromScreenId(screenId);
          const branding = channel
            ? configuration?.Brandings?.[channel]
            : undefined;

          return (
            <LinkWithLocale key={screenId} href={`${screenId}`}>
              <a
                className={styles.card}
                style={
                  {
                    "--channel-color": branding?.AppPrimaryColor,
                    "--channel-color-30": branding?.["AppPrimaryColor-30"],
                  } as React.CSSProperties
                }
              >
                <div className={styles.imageContainer}>
                  <AppImage
                    alt={media.Name || ""}
                    src={imageUrl || ""}
                    className={styles.image}
                  />
                </div>

                <div className={styles.content}>
                  <div className={styles.titleContainer}>
                    {icon && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img alt="" src={icon} className={styles.icon} />
                    )}
                    <div className={styles.title}>{media.Title}</div>
                  </div>
                  <div className={styles.description}>{media.Description}</div>
                </div>

                <Button
                  size="medium"
                  variant="subtle"
                  tabIndex={-1}
                  className={styles.button}
                >
                  {t("web_start_browse_button")}
                </Button>

                <Button
                  size="medium"
                  variant="subtle"
                  className={styles.chevron}
                  tabIndex={-1}
                  icon={<ChevronRightIcon />}
                />
              </a>
            </LinkWithLocale>
          );
        })}
      </div>
    </div>
  );
};

export default LandingPageChannels;
